import React, { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3, List, ListOrdered, Quote, Minus,
  Link as LinkIcon, ImagePlus, Link2, Undo2, Redo2,
  AlignLeft, AlignCenter, AlignRight, Eraser, Code2,
} from 'lucide-react';

const ToolbarBtn = ({ onClick, active = false, disabled = false, title, children, testid }) => (
  <button
    type="button"
    onMouseDown={e => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testid}
    className={`inline-flex items-center justify-center h-8 w-8 rounded transition-colors border border-transparent
      ${active ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-gray-100'}
      ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    {children}
  </button>
);

const Divider = () => <span className="inline-block w-px h-5 bg-gray-300 mx-1 align-middle" />;

// Max size for pasted / dropped / uploaded images (base64-encoded inline).
// Anything bigger is auto-downscaled to a max 1600px dimension before embedding.
const MAX_INLINE_BYTES = 800 * 1024; // 800 KB after base64
const MAX_DIMENSION = 1600;

const isImageFile = (file) => file && file.type && file.type.startsWith('image/');

/**
 * Load a File/Blob → optionally downscale via canvas → return base64 data URL.
 * Keeps aspect ratio, caps largest dimension at MAX_DIMENSION.
 */
const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Could not read image file'));
  reader.onload = () => {
    // If it's already small and JPEG/PNG, just return it as-is
    if (reader.result.length < MAX_INLINE_BYTES * 1.34) { // ~raw size threshold
      resolve(reader.result);
      return;
    }
    // Otherwise, downscale via canvas
    const img = new window.Image();
    img.onerror = () => reject(new Error('Invalid image data'));
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      // JPEG at 82% quality gives great compression for photos
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const RichTextEditor = ({ value, onChange, minHeight = 300, placeholder = 'Start typing…', testid = 'rte' }) => {
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false, underline: false }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({ inline: false, allowBase64: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none px-4 py-3 min-h-[200px]',
        'data-testid': `${testid}-content`,
      },
      // Drag & drop image onto the editor
      handleDrop: (view, event, _slice, moved) => {
        if (moved) return false; // moving existing node — let ProseMirror handle it
        const files = Array.from(event.dataTransfer?.files || []).filter(isImageFile);
        if (files.length === 0) return false;
        event.preventDefault();
        Promise.all(files.map(fileToDataUrl))
          .then(dataUrls => {
            dataUrls.forEach(src => {
              editor?.chain().focus().setImage({ src }).run();
            });
            toast.success(`${dataUrls.length} image${dataUrls.length > 1 ? 's' : ''} added`);
          })
          .catch(err => toast.error(err.message || 'Failed to add image'));
        return true;
      },
      // Paste image from clipboard
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItems = items.filter(i => i.type && i.type.startsWith('image/'));
        if (imageItems.length === 0) return false;
        event.preventDefault();
        const files = imageItems.map(i => i.getAsFile()).filter(Boolean);
        Promise.all(files.map(fileToDataUrl))
          .then(dataUrls => {
            dataUrls.forEach(src => {
              editor?.chain().focus().setImage({ src }).run();
            });
            toast.success('Image pasted');
          })
          .catch(err => toast.error(err.message || 'Failed to paste image'));
        return true;
      },
    },
  });

  // Sync external value changes (e.g. loading a template) without losing cursor during typing
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current && !editor.isFocused) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) {
    return (
      <div className="border rounded-lg bg-white" style={{ minHeight }}>
        <div className="px-4 py-8 text-sm text-gray-400">Loading editor…</div>
      </div>
    );
  }

  const promptLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Link URL (leave empty to remove):', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const promptImageUrl = () => {
    const url = window.prompt('Image URL (public https://…)');
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (e) => {
    const files = Array.from(e.target.files || []).filter(isImageFile);
    // Reset so the same file can be re-selected later
    e.target.value = '';
    if (files.length === 0) return;
    try {
      const dataUrls = await Promise.all(files.map(fileToDataUrl));
      dataUrls.forEach(src => {
        editor.chain().focus().setImage({ src }).run();
      });
      toast.success(`${dataUrls.length} image${dataUrls.length > 1 ? 's' : ''} added`);
    } catch (err) {
      toast.error(err.message || 'Failed to add image');
    }
  };

  return (
    <div className="border rounded-lg bg-white overflow-hidden" data-testid={testid}>
      {/* Hidden file input for the upload button */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileInputChange}
        className="hidden"
        data-testid={`${testid}-file-input`}
      />
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b bg-gray-50 sticky top-0 z-10">
        <ToolbarBtn title="Undo (Ctrl+Z)" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} testid={`${testid}-undo`}><Undo2 size={16} /></ToolbarBtn>
        <ToolbarBtn title="Redo (Ctrl+Y)" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} testid={`${testid}-redo`}><Redo2 size={16} /></ToolbarBtn>
        <Divider />
        <ToolbarBtn title="Heading 1" active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} testid={`${testid}-h1`}><Heading1 size={16} /></ToolbarBtn>
        <ToolbarBtn title="Heading 2" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} testid={`${testid}-h2`}><Heading2 size={16} /></ToolbarBtn>
        <ToolbarBtn title="Heading 3" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} testid={`${testid}-h3`}><Heading3 size={16} /></ToolbarBtn>
        <Divider />
        <ToolbarBtn title="Bold (Ctrl+B)" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} testid={`${testid}-bold`}><Bold size={16} /></ToolbarBtn>
        <ToolbarBtn title="Italic (Ctrl+I)" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} testid={`${testid}-italic`}><Italic size={16} /></ToolbarBtn>
        <ToolbarBtn title="Underline (Ctrl+U)" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon size={16} /></ToolbarBtn>
        <ToolbarBtn title="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={16} /></ToolbarBtn>
        <Divider />
        <ToolbarBtn title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} testid={`${testid}-ul`}><List size={16} /></ToolbarBtn>
        <ToolbarBtn title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} testid={`${testid}-ol`}><ListOrdered size={16} /></ToolbarBtn>
        <ToolbarBtn title="Blockquote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={16} /></ToolbarBtn>
        <ToolbarBtn title="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus size={16} /></ToolbarBtn>
        <Divider />
        <ToolbarBtn title="Align left" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}><AlignLeft size={16} /></ToolbarBtn>
        <ToolbarBtn title="Align center" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}><AlignCenter size={16} /></ToolbarBtn>
        <ToolbarBtn title="Align right" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}><AlignRight size={16} /></ToolbarBtn>
        <Divider />
        <ToolbarBtn title="Add or edit link" active={editor.isActive('link')} onClick={promptLink} testid={`${testid}-link`}><LinkIcon size={16} /></ToolbarBtn>
        <ToolbarBtn title="Upload image (or drag & drop / paste anywhere in editor)" onClick={triggerImageUpload} testid={`${testid}-image-upload`}><ImagePlus size={16} /></ToolbarBtn>
        <ToolbarBtn title="Insert image by URL" onClick={promptImageUrl} testid={`${testid}-image-url`}><Link2 size={16} /></ToolbarBtn>
        <ToolbarBtn title="Inline code" active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}><Code2 size={16} /></ToolbarBtn>
        <Divider />
        <ToolbarBtn title="Clear formatting" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}><Eraser size={16} /></ToolbarBtn>
      </div>

      {/* Editor surface — scrollable, preview-sized */}
      <div className="bg-white max-h-[620px] overflow-y-auto" style={{ minHeight }}>
        <EditorContent editor={editor} placeholder={placeholder} />
      </div>
    </div>
  );
};

export default RichTextEditor;
